/**
 * Rakenne Talotekniikka row chrome (#1555): one-open accordion + dirty Tallenna/Peruuta.
 * Extracted so bun:test can cover the rules without a DOM (frontend has no RTL).
 */
export {
  dirty_actions_visible,
  next_open_row_id as next_open_equipment_id,
  reconcile_open_row_id as reconcile_open_equipment_id,
} from "@/components/building/shared/one-open-accordion";

export type EquipmentStructureDraft = {
  name: string;
  equipment_type: string;
  equipment_tag: string;
  location: string;
  declared_variants: string[];
  declared_component_order: string[];
  declared_component_subtypes: Record<string, string>;
  declared_stream_component_order: Record<string, string[]>;
};

export function equipment_draft_from_card(equipment: {
  name: string;
  equipment_type: string;
  equipment_tag: string;
  location: string | null;
  declared_variants?: string[];
  declared_component_order?: string[];
  declared_component_subtypes?: Record<string, string>;
  declared_stream_component_order?: Record<string, string[]>;
}): EquipmentStructureDraft {
  return {
    name: equipment.name,
    equipment_type: equipment.equipment_type,
    equipment_tag: equipment.equipment_tag,
    location: equipment.location ?? "",
    declared_variants: [...(equipment.declared_variants ?? [])],
    declared_component_order: [...(equipment.declared_component_order ?? [])],
    declared_component_subtypes: {
      ...(equipment.declared_component_subtypes ?? {}),
    },
    declared_stream_component_order: clone_stream_order(
      equipment.declared_stream_component_order ?? {},
    ),
  };
}

export function is_equipment_draft_dirty(
  draft: EquipmentStructureDraft,
  saved: EquipmentStructureDraft,
): boolean {
  return (
    draft.name.trim() !== saved.name.trim() ||
    draft.equipment_type !== saved.equipment_type ||
    draft.equipment_tag.trim() !== saved.equipment_tag.trim() ||
    draft.location !== saved.location ||
    !same_string_set(draft.declared_variants, saved.declared_variants) ||
    !same_string_list(
      draft.declared_component_order,
      saved.declared_component_order,
    ) ||
    !same_string_record(
      draft.declared_component_subtypes,
      saved.declared_component_subtypes,
    ) ||
    !same_stream_order(
      draft.declared_stream_component_order,
      saved.declared_stream_component_order,
    )
  );
}

export function discard_equipment_draft(
  saved: EquipmentStructureDraft,
): EquipmentStructureDraft {
  return equipment_draft_from_card(saved);
}

function same_string_list(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, i) => value === b[i]);
}

function same_string_set(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sorted_a = [...a].sort();
  const sorted_b = [...b].sort();
  return sorted_a.every((value, i) => value === sorted_b[i]);
}

function same_string_record(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const keys_a = Object.keys(a).sort();
  const keys_b = Object.keys(b).sort();
  if (!same_string_list(keys_a, keys_b)) return false;
  return keys_a.every((key) => a[key] === b[key]);
}

function clone_stream_order(
  order: Record<string, string[]>,
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(order).map(([key, ids]) => [key, [...ids]]),
  );
}

function same_stream_order(
  a: Record<string, string[]>,
  b: Record<string, string[]>,
): boolean {
  const keys_a = Object.keys(a).sort();
  const keys_b = Object.keys(b).sort();
  if (!same_string_list(keys_a, keys_b)) return false;
  return keys_a.every((key) => same_string_list(a[key] ?? [], b[key] ?? []));
}
